import { useId, useState } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import { ChevronDown, ChevronUp, CircleCheck, CircleHelp, Info, TriangleAlert } from 'lucide-react';

import type { ActivationCheck, ActivationCheckStatus, ActivationChecks } from '../lib/activationReadiness';
import { NUMERIC, RADIUS, STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-611 — the one-card readiness summary at the top of the activation dialog.
//
// It answers "can I go ahead?" in two numbers, both COUNTED from
// `deriveActivationChecks` — never typed in. An unverifiable check is said to be
// unverified, not passed (FD-609), and a device-code restriction while the
// operator is using a different path is a note, not an item needing attention.
//
// ADVISORY. The card gates nothing; it is the operator's read of the same facts
// the server will check again when they activate.

/** One glyph per status. Colour is never the only carrier — every row also has words. */
export function CheckStatusIcon({ status, size = 16 }: { status: ActivationCheckStatus; size?: number }) {
  switch (status) {
    case 'pass':
      return <CircleCheck size={size} strokeWidth={1.5} color={STATUS.successText} aria-label="Passed" role="img" />;
    case 'attention':
      // The warning GLYPH hex is 2.03:1 on white — the label rung carries the stroke.
      return <TriangleAlert size={size} strokeWidth={1.5} color={STATUS.warningText} aria-label="Needs attention" role="img" />;
    case 'unverified':
      return <CircleHelp size={size} strokeWidth={1.5} color={TEXT.secondary} aria-label="Not verified" role="img" />;
    case 'note':
      return <Info size={size} strokeWidth={1.5} color={TEXT.secondary} aria-label="Note" role="img" />;
  }
}

function plural(n: number, one: string, many: string) {
  return n === 1 ? one : many;
}

export default function ActivationReadinessSummary({ checks }: { checks: ActivationChecks }) {
  const [open, setOpen] = useState(false);
  const listId = useId();

  const box: React.CSSProperties = {
    border: `1px solid ${STROKE.base}`,
    backgroundColor: SURFACE.rowFill,
    borderRadius: RADIUS.lg,
  };

  if (checks.loading) {
    return (
      <div className="flex items-center gap-3 px-4 py-3" style={box} data-testid="readiness-summary">
        <Spinner size="tiny" />
        <span className="text-[13px]" style={{ color: TEXT.secondary }}>
          Checking readiness — the minting account and the migration licence balance…
        </span>
      </div>
    );
  }

  const { passed, attention, unverified } = checks;
  const flagged: ActivationCheck[] = checks.rows.filter((r) => r.status !== 'pass');
  const firstAttention = checks.rows.find((r) => r.status === 'attention');
  const firstUnverified = checks.rows.find((r) => r.status === 'unverified');
  const lead = firstAttention ?? firstUnverified;

  return (
    <div style={box} data-testid="readiness-summary">
      <div className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 shrink-0">
            <CheckStatusIcon status="pass" size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold" style={{ color: TEXT.primary }} data-testid="readiness-passed">
              <span style={NUMERIC}>{passed}</span> {plural(passed, 'check', 'checks')} passed
            </div>
            <div className="text-[12px] leading-snug" style={{ color: TEXT.secondary }}>
              {attention === 0 && unverified === 0
                ? 'Nothing found that would stop this wave.'
                : 'Advisory — the server checks again when you activate.'}
            </div>
          </div>
        </div>

        {attention > 0 || unverified > 0 ? (
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 shrink-0">
              <CheckStatusIcon status={attention > 0 ? 'attention' : 'unverified'} size={18} />
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold" style={{ color: TEXT.primary }} data-testid="readiness-attention">
                {attention > 0 && (
                  <>
                    <span style={NUMERIC}>{attention}</span>{' '}
                    {plural(attention, 'item needs', 'items need')} attention
                  </>
                )}
                {attention > 0 && unverified > 0 && ' · '}
                {unverified > 0 && (
                  <>
                    <span style={NUMERIC}>{unverified}</span> not verified
                  </>
                )}
              </div>
              {lead && (
                <div className="text-[12px] leading-snug" style={{ color: TEXT.secondary }}>
                  {lead.label}: {lead.value}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div />
        )}

        {flagged.length > 0 ? (
          <Button
            appearance="subtle"
            size="small"
            aria-expanded={open}
            aria-controls={listId}
            aria-label={open ? 'Hide readiness items' : 'Show readiness items'}
            icon={open ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}
            onClick={() => setOpen((v) => !v)}
          />
        ) : (
          <div />
        )}
      </div>

      {open && flagged.length > 0 && (
        <ul
          id={listId}
          className="m-0 flex list-none flex-col gap-1.5 px-4 pb-3 pt-0"
          style={{ borderTop: `1px solid ${STROKE.subtle}`, paddingTop: 10 }}
          data-testid="readiness-items"
        >
          {flagged.map((row) => (
            <li key={row.id} className="flex items-start gap-2 text-[12.5px]">
              <span className="mt-px shrink-0">
                <CheckStatusIcon status={row.status} size={14} />
              </span>
              <span style={{ color: TEXT.primary }}>{row.label}</span>
              <span style={{ color: TEXT.secondary }}>
                — {row.value}
                {row.hint ? ` (${row.hint})` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
